import { useState, type ReactNode } from 'react'
import type { FormularioRepresentada } from '../../representadas/formularioRepresentada'
import { formasNaTela, alternarForma, adicionarFormaExtra, excluirFormaExtra } from '../../representadas/formasDePagamento'
import { IconeCartao, IconeCodigoBarras, IconeMaoMoeda, IconeNota, IconePix, IconeSetas, IconeTresPontos } from './IconesDaPolitica'
import {
  ESTILO_CAMPO, ESTILO_CARTAO_NOVO, ESTILO_ICONE_CARTAO, ESTILO_SELO_CINZA, ESTILO_SELO_VERDE, type PropsAba,
} from './estilosDaFornecedora'

const ICONE: Record<string, ReactNode> = {
  PIX: <IconePix />, Boleto: <IconeCodigoBarras />, 'Cartão de crédito': <IconeCartao />,
  Dinheiro: <IconeNota />, Transferência: <IconeSetas />, Antecipado: <IconeMaoMoeda />,
}

// Spec 27/09 (Parte 3): as 6 fixas comecam INATIVAS; [+] acrescenta outra
// (ja ativa); Excluir so nas acrescentadas. Tudo no `form` da aba: grava no
// Salvar, descarta no Descartar. `digitada` mora em FormRepresentada para o
// Salvar avisar se ficou texto esquecido (mesma escolha "1A" das condicoes).
export default function FormasPagamento({ form, setForm, editavel, digitada, setDigitada, alturaFixa = true }: PropsAba & {
  digitada: string
  setDigitada: (v: string) => void
  alturaFixa?: boolean
}) {
  const [menuAberto, setMenuAberto] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const lista = formasNaTela(form.formasAtivas, form.formasExtras)
  const mudar = (p: Partial<FormularioRepresentada>) => setForm((f) => ({ ...f, ...p }))

  function adicionar() {
    const r = adicionarFormaExtra(form.formasAtivas, form.formasExtras, digitada)
    if (!r.ok) { setErro(r.erro); return }
    mudar({ formasAtivas: r.formasAtivas, formasExtras: r.formasExtras })
    setDigitada('')
    setErro(null)
  }

  return (
    <div className={`${ESTILO_CARTAO_NOVO} ${alturaFixa ? 'h-[21rem]' : ''} flex flex-col min-w-0`} data-testid="secao-formas-pagamento">
      <div className="flex items-start gap-2">
        <span className={ESTILO_ICONE_CARTAO}><IconeCartao /></span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-800 leading-tight">Formas de pagamento</h3>
        </div>
      </div>
      <ul className="mt-2 flex-1 overflow-y-auto space-y-1.5 pr-0.5">
        {lista.map((f) => (
          <li key={f.nome} className="rounded-xl border border-slate-200 px-2 py-1.5">
            <div className="flex items-center gap-1.5">
              <span className="shrink-0 text-teal-700">{ICONE[f.nome] ?? <IconeCartao />}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-800 break-words leading-tight">{f.nome}</p>
                <span className={f.ativa ? ESTILO_SELO_VERDE : ESTILO_SELO_CINZA}>{f.ativa ? 'Ativo' : 'Inativo'}</span>
              </div>
              {editavel && (
                <button type="button" aria-label={`Ações de ${f.nome}`} aria-expanded={menuAberto === f.nome}
                  onClick={() => setMenuAberto((m) => (m === f.nome ? null : f.nome))}
                  className="shrink-0 text-slate-500 px-0.5"><IconeTresPontos /></button>
              )}
            </div>
            {menuAberto === f.nome && (
              <div className="mt-1 flex gap-2 justify-end">
                <button type="button" className="text-xs text-teal-800 underline"
                  onClick={() => { mudar(alternarForma(form.formasAtivas, form.formasExtras, f.nome)); setMenuAberto(null) }}>
                  {f.ativa ? 'Inativar' : 'Ativar'}
                </button>
                {!f.fixa && (
                  <button type="button" className="text-xs text-red-700 underline"
                    onClick={() => { mudar(excluirFormaExtra(form.formasAtivas, form.formasExtras, f.nome)); setMenuAberto(null) }}>
                    Excluir
                  </button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
      {editavel && (
        <div className="mt-2 space-y-1">
          <div className="flex gap-1">
            <input id="nova-forma" value={digitada} placeholder="Outra forma"
              onChange={(e) => { setDigitada(e.target.value); setErro(null) }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); adicionar() } }}
              className={`${ESTILO_CAMPO} text-sm py-1.5 min-w-0`} />
            <button type="button" onClick={adicionar} className="shrink-0 bg-teal-700 text-white rounded-lg px-2 text-xs">Adicionar</button>
          </div>
          {erro && <p className="text-xs text-red-600">{erro}</p>}
        </div>
      )}
    </div>
  )
}
